import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LOCALES } from '../../common/constants';
import {
    howItWorksImages,
    featureIcons,
    uiIcons,
    howItWorksFeatures,
    howItWorksIntroSection,
    howItWorksMainSection,
    whatYouCanDoSection,
    ctaSection
} from '../constant/how-it-works-sections.constant';

const HowItWorksComponent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const language = localStorage.getItem("i18nextLng");
    const pdfEnglish = 'https://invozioprod.blob.core.windows.net/files/user%20_manual_invozio__eng_light.pdf?sp=r&st=2025-04-24T11:17:36Z&se=2050-04-24T19:17:36Z&spr=https&sv=2024-11-04&sr=b&sig=qvGePPhQxSOb1KlbPlgwgotnbXXILHhlRh5vudjGXrc%3D';
    const pdfSwedish = 'https://invozioprod.blob.core.windows.net/files/user%20_manual_invozio__swe_light.pdf?sp=r&st=2025-04-24T11:20:10Z&se=2050-04-24T19:20:10Z&spr=https&sv=2024-11-04&sr=b&sig=Z88XAYoDo%2F8YiFEg7hoBsw8BzUkH%2FaxYkSmY02TJR%2Fc%3D';

    const handleDownloadEnglish = () => {
        window.open(pdfEnglish, '_blank');
    };

    const handleDownloadSwedish = () => {
        window.open(pdfSwedish, '_blank');
    };

    const handleCreateAccount = () => {
        navigate(
            language === LOCALES.EN_US
                ? `/emailregister?lang=${LOCALES.EN_US}`
                : `/e-postregister?lang=${LOCALES.SV_SE}`
        );
    };
    return (
        <div className="how-it-works-page px-0">
            {/* Intro Section */}
            <section className="intro-section px-0">
                <Container>
                    <Row className="intro-row px-0">
                        <Col md={6} className="intro-left px-0">
                            <h2 className="intro-title py-0 my-0">
                                {t('Simple, Smooth Invoicing No Company Needed')}
                            </h2>
                        </Col>
                        <Col md={6} className="intro-right  px-0">
                            <div className="intro-description">
                                <p>
                                    {t("Invoicing without a company should be simple and smooth. At Invozio, we have made the process as intuitive and user-friendly as possible. Here's an overview of how invoicing with us works:")}
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            <section className="intro-section px-0">
                <Container>
                    <Row className="intro-row px-0">
                        <Col md={6} className="intro-left px-0">
                            <h2 className="intro-title">
                                {t("Here's How It Works")}
                            </h2>
                        </Col>
                        <Col md={6} className="intro-right px-0">
                            <div className="intro-description">
                                <p>
                                    {t("Invozio is a fast-growing platform designed for freelancers and solo professionals, providing a secure and effortless way to manage business without registering a company. This step-by-step guide (PDF) will walk you through everything you need to get started from creating and sending invoices to tracking payments and managing expenses. Download it to make the most of Invozio and take control of your finances with confidence.")}
                                </p>
                            </div>
                        </Col>
                    </Row>

                    {/* Download Buttons Row - positioned under the intro description */}
                    <Row className="download-buttons-row mt-4">
                        <Col md={{ span: 6, offset: 6 }} className='px-0 g-0' > 
                            <Row className='px-0 mx-0 d-flex'>
                                <Col sm={6} className='px-0'>
                                    <button className="download-btn primary px-0" onClick={handleDownloadEnglish}>
                                        <span className="btn-text">
                                            {t('Download Full Guide (English PDF)')}
                                        </span>
                                        <img
                                            src={uiIcons.download}
                                            alt="Download"
                                            className="download-icon"
                                        />
                                    </button>
                                </Col>
                                <Col sm={6} className='px-0'>
                                    <button className="download-btn secondary px-0 ml-sm-5 pl-md-0 ml-sm-0 pl-sm-5 pl-0 ml-0" onClick={handleDownloadSwedish}>
                                        <span className="btn-text">
                                            {t('Download Full Guide (Swedish PDF)')}
                                        </span>
                                        <img
                                            src={uiIcons.download}
                                            alt="Download"
                                            className="download-icon"
                                        />
                                    </button>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Hero Image */}
            <section className="hero-image-section px-0">
                <Container>
                    <img
                        src={howItWorksImages.heroImage}
                        alt="How Invozio Works"
                        className="hero-image"
                    />
                </Container>
            </section>

            {/* Separation Line */}
            <div className="separation-line"></div>

            {/* Separation Line */}
            <div className="separation-line"></div>

            {/* Features Section */}

            <section className="intro-section px-0">
                <Container>
                    <Row className="intro-row px-0">
                        <Col md={6} className="intro-left px-0">
                            <h2 className="intro-title">
                                {t('What You Can Do with Invozio')}
                            </h2>
                        </Col>
                        <Col md={6} className="intro-right px-0">
                            <div className="intro-description">
                                <p>
                                    {t('Explore the core features that make invoicing without a company simple, fast, and efficient.')}
                                </p>
                                <p>
                                    {t('From creating invoices and managing expenses to submitting travel reports and allowances. Invozio gives freelancers everything they need to run their business smoothly.')}
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            <section className="features-section px-0">
                <Container>
                    <div className="features-content">
                        {/* Features Header */}
                    
                        {/* Features Grid */}
                        <div className="features-grid">
                            {/* First Row */}
                            <Row className="features-row">
                                {howItWorksFeatures.slice(0, 4).map((feature, index) => (
                                    <Col key={index} sm={6} lg={3} className="feature-item">
                                        <img
                                            src={featureIcons[feature.iconKey as keyof typeof featureIcons]}
                                            alt={t(feature.titleKey)}
                                            className="feature-icon"
                                        />
                                        <div className="feature-content">
                                            <h3 className="feature-title">
                                                {t(feature.titleKey)}
                                            </h3>
                                            <p className="feature-description px-0">
                                                {t(feature.descriptionKey)}
                                            </p>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Second Row */}
                            <Row className="features-row">
                                {howItWorksFeatures.slice(4, 8).map((feature, index) => (
                                    <Col key={index + 4} sm={6} lg={3} className="feature-item px-0">
                                        <img
                                            src={featureIcons[feature.iconKey as keyof typeof featureIcons]}
                                            alt={t(feature.titleKey)}
                                            className="feature-icon"
                                        />
                                        <div className="feature-content">
                                            <h3 className="feature-title">
                                                {t(feature.titleKey)}
                                            </h3>
                                            <p className="feature-description px-0">
                                                {t(feature.descriptionKey)}
                                            </p>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="cta-section px-0">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} xl={7} className="cta-content px-0">
                            <div className="cta-text">
                                <h2 className="cta-title">
                                    {t('Ready to Get Started?')}
                                </h2>
                                <p className="cta-description">
                                    {t('Join thousands of freelancers using Invozio to invoice without a company. It\'s fast, secure, and completely free to create an account.')}
                                </p>
                            </div>
                            <button className="cta-button" onClick={handleCreateAccount}>
                                <span className="cta-btn-text">
                                    {t('Create Free Account')}
                                </span>
                                <img
                                    src={uiIcons.rightArrow}
                                    alt="Right Arrow"
                                    className="cta-arrow-icon"
                                />
                            </button>
                        </Col>
                        <Col lg={6} xl={5} className="cta-image-col d-none d-lg-block px-0">
                            <img
                                src={howItWorksImages.ctaBackground}
                                alt="Get Started"
                                className="cta-background"
                            />
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default HowItWorksComponent;
